/**
 * Task detail view container - fetches task and renders detail.
 */

import { useTask } from '../hooks/useTasks';
import { TaskDetail } from './TaskDetail';
import { TaskCardSkeleton } from './Skeleton';
import { ErrorState } from './ErrorState';
import type { ApiRequestError } from '../lib/api';

interface TaskDetailViewProps {
  taskId: string | null;
  onBack: () => void;
}

export function TaskDetailView({ taskId, onBack }: TaskDetailViewProps) {
  const { data, isLoading, isError, error, refetch } = useTask(taskId);

  if (!taskId) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-ink-muted">No task selected</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-4">
        <TaskDetailSkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-4">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 text-ink-muted hover:text-ink transition-colors mb-4"
        >
          <BackIcon className="w-5 h-5" />
          <span className="text-sm font-medium">Back to Tasks</span>
        </button>
        <ErrorState error={error as ApiRequestError} onRetry={refetch} />
      </div>
    );
  }

  if (!data?.data) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-ink-muted">Task not found</p>
      </div>
    );
  }

  return (
    <TaskDetail
      task={data.data}
      onBack={onBack}
      onTaskUpdated={refetch}
    />
  );
}

function TaskDetailSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      {/* Header skeleton */}
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-2">
            <div className="skeleton h-7 w-32" />
            <div className="skeleton h-4 w-24" />
          </div>
          <div className="skeleton h-6 w-20 rounded" />
        </div>
        <div className="skeleton h-5 w-full" />
        <div className="skeleton h-5 w-2/3" />
      </div>

      {/* Info cards skeleton */}
      <div className="grid grid-cols-2 gap-3">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="bg-surface-raised border border-rule rounded-lg p-3"
          >
            <div className="skeleton h-4 w-16 mb-2" />
            <div className="skeleton h-5 w-24" />
          </div>
        ))}
      </div>

      {/* Button skeleton */}
      <div className="skeleton h-14 w-full rounded-xl" />
    </div>
  );
}

function BackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
        clipRule="evenodd"
      />
    </svg>
  );
}
